import { useCallback, useMemo, useRef } from 'react';

import type { TrackingFn } from '@change-corgi/core-react/tracking';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';
import type { ShareActionsOptions, ShareTrackingDataOverride } from '#src/app/shared/hooks/share';
import { openFacebookShare, tapFacebookShare } from '#src/app/shared/share/facebook';
import { getShareUrlInfo } from '#src/app/shared/share/utils';
import { copyToClipboard } from '#src/app/shared/utils/clipboard';

import { buildFacebookPostPayload } from './buildFacebookPostPayload';
import { buildSheetShareOptions, useFeedHandoff, useGroupHandoff } from './facebookPostSheetHandoffs';
import { type FacebookPostSheetCopyTrigger, useFacebookPostSheetTracking } from './share/tracking';

/**
 * What opened the sheet. Carries the tap's `share_placement`, so the composer-open event matches it,
 * and whether `copyAtTap` put the post on the clipboard, which the sheet shows as its copied pill.
 */
export type FacebookPostSheetTap = Readonly<{ trackingData?: ShareTrackingDataOverride; copied?: boolean }>;

export type UseFacebookPostSheetResult = {
	/** The cleaned caption to preview, or `null` when the sheet has nothing to post and must not open. */
	caption: string | null;
	/**
	 * Copies the payload at the Facebook tap that opens the sheet, before the treat wait. Must be called
	 * synchronously from the tap handler, for the same iOS Safari reason as `postToFeed`. Fires no event:
	 * `tapFacebook` records the copy after the treat, so the event carries the arm's stamp.
	 */
	copyAtTap: () => Promise<boolean>;
	/**
	 * The Facebook tap that opens the sheet: cancels a pending copy-link redirect, as the share pipeline
	 * does, and fires `share_petition_intent` and `share_petition` with the production tracking data,
	 * then the copied event with the result of `copyAtTap`. Opens nothing.
	 */
	tapFacebook: (tap: FacebookPostSheetTap) => void;
	/**
	 * The `variant_1` Post button, and the `variant_2` Continue tap with the feed picked. Copies the payload
	 * with the `post_cta` trigger, unless a copy in the flow already succeeded, and opens the Facebook
	 * composer in the same tap, then runs the page's post-share steps. Must be called synchronously from the tap handler: iOS Safari
	 * blocks a clipboard write or a `window.open` that runs after the tap's user activation ends.
	 */
	postToFeed: (tap: FacebookPostSheetTap) => void;
	/**
	 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment. The production Facebook share for
	 * a tap that waits for the treat (`control`, `fake_control`). Opens the
	 * composer inside the tap, and holds every event, and `onSuccess`, until `treatSettled` settles. The
	 * pipeline cannot do this: it awaits `onBeforeAction` and then fires the events and opens the
	 * composer in one call, and iOS Safari blocks a composer that opens after a network wait. Same
	 * options, events, and order as the pipeline's Facebook share. Must be called synchronously from the
	 * tap handler. Resolves to whether the composer opened.
	 */
	shareAtTap: (tap: FacebookPostSheetTap, treatSettled: Promise<unknown>) => Promise<boolean>;
	/**
	 * A `variant_2` group-card tap: records the tap and copies the payload, so the clipboard holds it
	 * before the group opens. Opens nothing. Resolves to whether the copy succeeded.
	 */
	selectGroup: (group: FacebookGroupRecommendation) => Promise<boolean>;
	/**
	 * The `variant_2` group CTA, the second of the two taps. Copies the payload and opens the group,
	 * both inside the tap, then fires `share_petition_success` with the group destination and
	 * runs the page's post-share steps. Must be called synchronously from the tap handler.
	 */
	postToGroup: (tap: FacebookPostSheetTap, group: FacebookGroupRecommendation) => void;
	/** Ends the sheet's flow, so the next Facebook tap starts with no copy. Call it when the sheet closes. */
	resetCopy: () => void;
	trackViewed: () => void;
	trackDismissed: () => void;
	trackGroupCardViewed: (groupUrl: string) => void;
};

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment. The body of `shareAtTap`. Holds the
 * tap's events until the treat settles: `share_petition_intent` and `share_petition` go first, and a
 * rejected wait still sends them, because the share went ahead. Every later event, and `onSuccess`,
 * waits behind them on the same promise, so the order stays the pipeline's.
 */
async function shareFacebookAtTap({
	url,
	options,
	treatSettled,
	utilityContext,
	track,
	shareOptions,
}: {
	url: string;
	options: Parameters<typeof openFacebookShare>[1];
	treatSettled: Promise<unknown>;
	utilityContext: UtilityContext;
	track: TrackingFn;
	shareOptions: ShareActionsOptions;
}): Promise<boolean> {
	const tapEventsSent = (async () => {
		try {
			await treatSettled;
		} catch {
			// The share went ahead: send its events anyway.
		}
		tapFacebookShare(options, utilityContext, track);
	})();
	const trackAfterTapEvents: TrackingFn = async (...args) => {
		await tapEventsSent;
		return track(...args);
	};

	// Reaches `window.open` before its first await, so the open stays inside the tap.
	const opened = await openFacebookShare(url, options, utilityContext, trackAfterTapEvents);
	void (async () => {
		// `onSuccess` fires its own events (promotion share, new-share record), so it waits too.
		await tapEventsSent;
		await (opened ? shareOptions.onSuccess : shareOptions.onFailure)?.('facebook');
	})();
	return opened;
}

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment. Writes the post once per sheet flow.
 * `writeOncePerFlow` skips the write once a copy in the flow succeeded, and otherwise writes,
 * synchronously. A copy still in flight is not reused: a later tap, such as the group CTA, must write
 * inside its own tap in case that copy fails. `resetCopy` ends the flow.
 */
function useCopyOncePerFlow(writePayload: () => Promise<boolean>) {
	// One object per flow, so a copy that settles after `resetCopy` marks only the flow it started in.
	const flowRef = useRef({ copied: false });

	const writeOncePerFlow = useCallback(() => {
		if (flowRef.current.copied) return { writing: Promise.resolve(true), wrote: false };
		const flow = flowRef.current;
		const writing = writePayload();
		void (async () => {
			if (await writing) flow.copied = true;
		})();
		return { writing, wrote: true };
	}, [writePayload]);

	const resetCopy = useCallback(() => {
		flowRef.current = { copied: false };
	}, []);

	return { writeOncePerFlow, resetCopy };
}

/**
 * Handlers for the Facebook post sheet (CHANGE-99696). The composer open reuses the page's share
 * options, so the sheet fires the same `share_petition_success` event, with the same properties, as
 * the production Facebook share, and then runs the same `onSuccess` (promotion share, new-share
 * record, redirect). `tapFacebook` fires `share_petition_intent` and `share_petition` at the Facebook
 * tap, and `postToFeed` fires only `share_petition_success`, so each event fires once.
 *
 * Every copy, from the Facebook tap to either CTA, writes the post once per flow: after a copy
 * succeeded, a later trigger writes nothing and fires no copied event. Until then, each trigger
 * writes. The flow ends at `resetCopy`, when the sheet closes.
 */
export function useFacebookPostSheet({
	caption,
	shareOptions,
}: {
	caption: string | null;
	shareOptions: ShareActionsOptions;
}): UseFacebookPostSheetResult {
	const utilityContext = useUtilityContext();
	const track = useTracking();
	const { trackViewed, trackCopied, trackDismissed, trackGroupCardViewed } = useFacebookPostSheetTracking();
	const {
		data: {
			petition: { shareLinks },
		},
	} = usePsfPromoteOrSharePageData();

	const facebookLink = shareLinks.links.find((link) => link.channel === 'facebook');
	const urlInfo = useMemo(() => (facebookLink ? getShareUrlInfo(facebookLink) : null), [facebookLink]);
	const trimmedCaption = caption?.trim() || null;
	const payload = buildFacebookPostPayload(trimmedCaption, urlInfo?.url ?? null);

	// No prompt fallback. From a tap that opens something, a blocking `window.prompt` can use up the
	// tap's user activation on iOS Safari, and the composer open or the sheet that follows would then
	// be blocked. From a group card, an arrow key moves the radio selection and copies again, so a
	// failing clipboard would prompt on every key press.
	const writePayload = useCallback(
		async () => (payload ? copyToClipboard(payload, utilityContext.i18n, { fallbackToPrompt: false }) : false),
		[payload, utilityContext.i18n],
	);

	const { writeOncePerFlow, resetCopy } = useCopyOncePerFlow(writePayload);

	const copy = useCallback(
		async (trigger: FacebookPostSheetCopyTrigger) => {
			if (!payload) return false;
			const { writing, wrote } = writeOncePerFlow();
			const copied = await writing;
			// A failure is tracked too: a CTA copy fails silently, after the composer opens, so the event is
			// the only record of it. A reused copy is tracked by the trigger that wrote it.
			if (wrote) trackCopied(trigger, copied);
			return copied;
		},
		[payload, writeOncePerFlow, trackCopied],
	);

	const copyAtTap = useCallback(async () => writeOncePerFlow().writing, [writeOncePerFlow]);

	const tapFacebook = useCallback(
		(tap: FacebookPostSheetTap) => {
			if (!urlInfo) return;
			shareOptions.onBeforeShare?.('facebook');
			tapFacebookShare(buildSheetShareOptions(shareOptions, urlInfo.trackingProperties, tap), utilityContext, track);
			if (tap.copied !== undefined) trackCopied('facebook_tap', tap.copied);
		},
		[urlInfo, shareOptions, utilityContext, track, trackCopied],
	);

	const { postToFeed } = useFeedHandoff({ payload, urlInfo, shareOptions, copy });

	const shareAtTap = useCallback(
		async (tap: FacebookPostSheetTap, treatSettled: Promise<unknown>) =>
			urlInfo
				? shareFacebookAtTap({
						url: urlInfo.url,
						options: buildSheetShareOptions(shareOptions, urlInfo.trackingProperties, tap),
						treatSettled,
						utilityContext,
						track,
						shareOptions,
					})
				: false,
		[urlInfo, utilityContext, track, shareOptions],
	);

	const { selectGroup, postToGroup } = useGroupHandoff({ payload, urlInfo, shareOptions, copy });

	return {
		caption: payload ? trimmedCaption : null,
		copyAtTap,
		tapFacebook,
		postToFeed,
		shareAtTap,
		selectGroup,
		postToGroup,
		resetCopy,
		trackViewed,
		trackDismissed,
		trackGroupCardViewed,
	};
}
